// Classification helpers for the Summary tab's candidate detail view — split
// a form submission's answers into "free text" vs "numerical parameter"
// buckets so they can be shown as two flat, attributed lists across every
// form/evaluator instead of one card per submission.

export function humanLabel(key: string): string {
  return key.replace(/([A-Z])/g, ' $1').replace(/[_-]/g, ' ').trim().replace(/^\w/, c => c.toUpperCase());
}

export interface FieldMeta {
  title: string;
  /** 'grade' = FormBuilder's 'gradebox' (numeric); everything else (free
   * text, select, or an unrecognized/legacy field shape) is 'text'. */
  kind: 'grade' | 'text';
  maxGrade?: number;
}

export interface FormAnswerEntry {
  formName: string;
  evaluator: string;
  label: string;
  value: string;
  /** True when this answer came from a generic-type form whose roleRelevancy
   * includes 'interviewer' — drives the Free Text grid's accent color. */
  isInterviewerForm?: boolean;
}

/**
 * A "interview form" for coloring purposes is specifically a *generic*-type
 * form scoped to interviewers via roleRelevancy — not the dedicated
 * formType: 'interview' (a separate form type with its own scoring
 * strategy). Used to tag entries so the Free Text grid can visually set
 * these apart from other evaluator forms.
 */
export function isInterviewerRelevantForm(formType: string | undefined, roleRelevancy: string[] | undefined): boolean {
  return formType === 'generic' && !!roleRelevancy?.includes('interviewer');
}

export interface NumericAnswerEntry extends Omit<FormAnswerEntry, 'value'> {
  value: number;
  max: number | null;
}

const SKIP_KEYS = new Set(['position', 'team-size', 'teamSize', 'team_size', 'section']);

/**
 * Classifies one submission's parsed fields (+ its overall rating, if any)
 * into Free Text vs Numerical Parameters. A field only counts as numeric
 * when its form-structure definition says so (kind: 'grade') AND its value
 * actually parses as a number — otherwise it falls back to free text rather
 * than being silently dropped. Flag booleans and flag-associated note
 * fields are excluded entirely; those are surfaced separately (Concerns).
 */
export function classifySubmissionAnswers(
  fields: Record<string, string>,
  rating: number | null,
  formName: string,
  evaluator: string,
  ratingLabel: string,
  metaMap: Map<string, FieldMeta>,
  flagNoteKeys: Set<string>
): { text: FormAnswerEntry[]; numeric: NumericAnswerEntry[] } {
  const text: FormAnswerEntry[] = [];
  const numeric: NumericAnswerEntry[] = [];

  if (rating != null && !Number.isNaN(rating)) {
    numeric.push({ formName, evaluator, label: ratingLabel, value: rating, max: rating > 10 ? 100 : 10 });
  }

  for (const [key, value] of Object.entries(fields)) {
    if (!value || !value.trim()) continue;
    if (value === 'true' || value === 'false') continue; // flag booleans — shown under Concerns, not here
    if (SKIP_KEYS.has(key) || flagNoteKeys.has(key)) continue;

    const meta = metaMap.get(key);
    const label = meta?.title ?? humanLabel(key);

    if (meta?.kind === 'grade') {
      const num = Number(value);
      if (!Number.isNaN(num)) {
        numeric.push({ formName, evaluator, label, value: num, max: meta.maxGrade ?? null });
        continue;
      }
    }
    text.push({ formName, evaluator, label, value });
  }

  return { text, numeric };
}
